// Typed API calls for the jobs domain and upload portal.
import { apiRequest } from './client';
import { getIdClaims } from '../auth/tokens';
import type {
  Job,
  JobDetail,
  JobCreateInput,
  JobStatus,
  UploadContext,
  EtoeContext,
  PresignedTarget,
  ValuationInputs,
  ClientFormContext,
  StaffMember,
} from '../types';

export interface BuildReportResult {
  status: string;
  reportDocId: string;
  filename: string;
  delivered: boolean;
  pdfBase64: string;
}
export function buildReport(
  jobId: string,
  input: { valuation: ValuationInputs; deliver?: boolean },
): Promise<BuildReportResult> {
  return apiRequest(`/jobs/${encodeURIComponent(jobId)}/report`, { method: 'POST', body: input });
}

export function listJobs(status?: JobStatus): Promise<{ jobs: Job[] }> {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiRequest<{ jobs: Job[] }>(`/jobs${q}`);
}

export function getJob(jobId: string): Promise<JobDetail> {
  return apiRequest<JobDetail>(`/jobs/${encodeURIComponent(jobId)}`);
}

export function createJob(input: JobCreateInput): Promise<Job> {
  const claims = getIdClaims();
  const createdByName = claims.name || claims.email;
  return apiRequest<Job>('/jobs', { method: 'POST', body: { ...input, createdByName } });
}

export function updateJob(jobId: string, patch: Partial<Job>): Promise<Job> {
  return apiRequest<Job>(`/jobs/${encodeURIComponent(jobId)}`, { method: 'PATCH', body: patch });
}

export function issueUploadLink(jobId: string): Promise<{ uploadUrl: string; uploadLinkExpiresAt?: string }> {
  return apiRequest(`/jobs/${encodeURIComponent(jobId)}/upload-link`, { method: 'POST' });
}

// reuseLink=true re-sends the same still-valid upload link (bubble "Re-send");
// omit or false to mint a fresh 30-day link (first send, resend after edit, or
// re-issue after expiry).
export function sendQuote(jobId: string, reuseLink = false): Promise<{ ok: true }> {
  return apiRequest(`/jobs/${encodeURIComponent(jobId)}/quote/send`, {
    method: 'POST',
    body: { reuseLink },
  });
}

export interface QueryEmailInput {
  subject?: string;
  html: string;
  attachments?: { filename: string; contentType?: string; contentBase64: string }[];
  attachDocIds?: string[];
}

// Sends the clarification email composed in the in-app editor (HTML + attachments)
// and moves the job to QUERIES_SENT.
export function sendQueries(jobId: string, email: QueryEmailInput): Promise<{ status: string }> {
  return apiRequest(`/jobs/${encodeURIComponent(jobId)}/queries/send`, {
    method: 'POST',
    body: email,
  });
}

export function rerunJob(jobId: string): Promise<{ ok: true }> {
  return apiRequest(`/jobs/${encodeURIComponent(jobId)}/rerun`, { method: 'POST' });
}

// --- Public eTOE (proposal) signing (no auth) ---

export function getEtoeContext(token: string): Promise<EtoeContext> {
  return apiRequest<EtoeContext>(`/etoe/${encodeURIComponent(token)}`, { auth: false });
}

export function signEtoe(token: string, signatureName: string): Promise<{ status: string; signedAt: string }> {
  return apiRequest(`/etoe/${encodeURIComponent(token)}`, {
    method: 'POST',
    auth: false,
    body: { signatureName, accepted: true },
  });
}

// --- Public upload portal (no auth) ---

export function getUploadContext(token: string): Promise<UploadContext> {
  return apiRequest<UploadContext>(`/upload/${encodeURIComponent(token)}`, { auth: false });
}

export function presignUploads(
  token: string,
  files: { filename: string; contentType: string; requirement?: string }[],
): Promise<PresignedTarget[]> {
  // upload-presign returns a bare array of { docId, filename, url }.
  return apiRequest(`/upload/${encodeURIComponent(token)}`, {
    method: 'POST',
    auth: false,
    body: { files },
  });
}

// POST a file to S3 using its presigned POST form (fields + file). S3 enforces the
// size cap via the signed content-length-range condition. Reports progress via
// callback; uses XMLHttpRequest because fetch has no upload-progress events.
export function postToPresigned(
  target: { url: string; fields: Record<string, string> },
  file: File,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    // Only the presigned fields are allowed by the POST policy; adding an extra
    // field (e.g. Content-Type) makes S3 reject the upload with 403.
    for (const [k, v] of Object.entries(target.fields)) form.append(k, v);
    form.append('file', file); // file MUST be last for S3 POST policy
    const xhr = new XMLHttpRequest();
    xhr.open('POST', target.url, true);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve();
      } else if (xhr.status === 403 && /EntityTooLarge|content-length/i.test(xhr.responseText || '')) {
        reject(new Error('This file is too large. The maximum is 25 MB per file.'));
      } else {
        reject(new Error(`Upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
    xhr.send(form);
  });
}

export interface DocumentDownload {
  url: string;
  filename: string;
  contentType?: string;
  size?: number;
  macroEnabled: boolean;
  expiresInSeconds: number;
}

/** Mint a short-lived presigned GET for one document on a job. */
export async function getDocumentDownload(jobId: string, docId: string) {
  return apiRequest<DocumentDownload>(
    `/jobs/${encodeURIComponent(jobId)}/documents/${encodeURIComponent(docId)}/download`,
  );
}

/** Public: load whichever client form a portal token opens. */
export async function getClientFormContext(token: string) {
  return apiRequest<ClientFormContext>(`/client/${encodeURIComponent(token)}`, { auth: false });
}

/** Public: submit it. The server rejects a partial submission. */
export async function submitClientForm(token: string, body: unknown) {
  return apiRequest<{ jobId: string; kind: string }>(`/client/${encodeURIComponent(token)}`, {
    method: 'POST',
    body,
    auth: false,
  });
}

/** Staff: mint a client-form link and email it. */
export async function issueClientForm(jobId: string, kind: 'queries' | 'intake') {
  return apiRequest<{ url: string; kind: string; expiresAt: string }>(
    `/jobs/${encodeURIComponent(jobId)}/client-form`,
    { method: 'POST', body: { kind } },
  );
}

/** Staff this practice can assign an engagement to. */
export async function listStaff() {
  return apiRequest<{ staff: StaffMember[] }>('/staff');
}

/** Move an engagement to another staff member. Changes whose mailbox sends. */
export async function reassignJob(jobId: string, ownerSub: string) {
  return apiRequest<{ ownerSub: string; ownerName: string }>(
    `/jobs/${encodeURIComponent(jobId)}/owner`,
    { method: 'POST', body: { ownerSub } },
  );
}

/**
 * Rebuild Valuation Workpaper.xlsx on demand, carrying the valuation figures, the
 * client's questionnaire and their answers to the queries. The workbook is first
 * generated at extraction time, before any of that exists.
 */
export function buildWorkpaper(
  jobId: string,
  body: { valuation?: ValuationInputs },
): Promise<{ jobId: string; result: string }> {
  return apiRequest(`/jobs/${encodeURIComponent(jobId)}/workpaper`, {
    method: 'POST',
    body,
  });
}

/**
 * The two ways out of "documents insufficient".
 *
 * `chase` emails the client naming the gap; `accept` overrules the extraction and
 * builds the workbook on the years actually supplied.
 */
export function resolveDocsGap(
  jobId: string,
  body:
    | { action: 'chase'; subject: string; html: string }
    | { action: 'accept'; note?: string },
): Promise<{ jobId: string; action: string; sent?: boolean; result?: string }> {
  return apiRequest(`/jobs/${encodeURIComponent(jobId)}/documents/gap`, {
    method: 'POST',
    body,
  });
}

/**
 * The client says they have finished sending documents.
 *
 * Uploading and being finished are different states, and the job page could not tell
 * them apart. Records who made the call and starts the read immediately.
 */
export function completeUpload(token: string): Promise<{ jobId: string; submitted: boolean }> {
  return apiRequest(`/upload/${encodeURIComponent(token)}/complete`, {
    method: 'POST',
    auth: false,
    body: {},
  });
}

/**
 * Permanently delete a job and everything belonging to it.
 *
 * Not a soft delete. The job's records, its tokens, its email pointers and every S3
 * object version under its prefix all go. The typed confirmation is checked on the
 * server too, because a DELETE guarded only by the UI is one fetch call from being
 * fired by accident.
 */
export function deleteJob(
  jobId: string,
  confirm: string,
): Promise<{
  jobId: string;
  entityName: string;
  deleted: { jobItems: number; tokensAndPointers: number; s3Objects: number };
  notRemoved: string[];
}> {
  return apiRequest(`/jobs/${encodeURIComponent(jobId)}`, { method: 'DELETE', body: { confirm } });
}
